import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';

interface ChangeSequenceDialogProps {
  open: boolean;
  currentNumber: number;
  maxNumber: number;
  onConfirm: (newNumber: number) => void;
  onCancel: () => void;
}

export const ChangeSequenceDialog: React.FC<ChangeSequenceDialogProps> = ({
  open,
  currentNumber,
  maxNumber,
  onConfirm,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState(String(currentNumber));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setInputValue(String(currentNumber));
      setError('');
    }
  }, [open, currentNumber]);

  const handleConfirm = () => {
    const newNumber = parseInt(inputValue);
    
    if (isNaN(newNumber)) {
      setError('Please enter a valid number');
      return;
    }
    if (newNumber < 1 || newNumber > maxNumber) {
      setError(`Number must be between 1 and ${maxNumber}`);
      return;
    }
    if (newNumber === currentNumber) {
      setError('Please choose a different number');
      return;
    }
    
    onConfirm(newNumber);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    // Explicitly allow backspace and delete
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.stopPropagation();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Marker Sequence</DialogTitle>
          <DialogDescription>
            Change marker #{currentNumber} to a new position. Other markers will be renumbered automatically.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="newNumber">
              New Sequence Number (1-{maxNumber})
            </Label>
            <input
              id="newNumber"
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              autoFocus
              className="flex h-10 w-full rounded-md border border-border bg-input px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
          
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Example:</p>
            {(() => {
              const num = parseInt(inputValue);
              if (isNaN(num)) return <p>Enter a valid number to see preview</p>;
              if (num > currentNumber) {
                return (
                  <p>
                    Markers {currentNumber + 1}-{num} will shift down to {currentNumber}-{num - 1}
                  </p>
                );
              } else if (num < currentNumber) {
                return (
                  <p>
                    Markers {num}-{currentNumber - 1} will shift up to {num + 1}-{currentNumber}
                  </p>
                );
              } else {
                return <p>No changes will be made</p>;
              }
            })()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Change Sequence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

